<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { useChecklistControllerStore } from "~/composables/useChecklistControllerStore";
import { useDocumentContainerRightPosition } from "~/composables/useDocumentContainerRightPosition";
import { useSystemCommentsStore } from "~/composables/useSystemCommentsStore";
import { useAnnotationFormState } from "~/stores/useAnnotationFormState";
import { useUserAnnotations } from "~/stores/useUserAnnotations";
import { AnnotationTypes } from "~/types/enums/AnnotationTypes.enum";
import { RevisionAnnotation } from "~/types/RevisionAnnotation";

const userAnnotatinStore = useUserAnnotations();
const { activeAnnotations, annotationDefinition } =
  storeToRefs(userAnnotatinStore);

const annotationFormStateStore = useAnnotationFormState();

const {
  setAnnotationBeingEdited,
  updateAnnotationType,
  createDefaultAnnotation,
} = annotationFormStateStore;

const { isAnnotationBeingEdited, annotationBeingEdited } = storeToRefs(
  annotationFormStateStore
);

const documentId = inject("documentId") as any;
const { saveAnnotation, deleteAnnotation } = useSaveAnnotation(documentId);

const save = () => {
  if (annotation.value?.commentObject == undefined) {
    clear();
    return;
  }

  saveAnnotation(
    { ...annotation.value! },
    useIsAnnotationCommentLong(annotation.value!)
  );

  clear();
};

const clear = () => {
  setAnnotationBeingEdited(documentId, undefined);
  annotation.value = undefined;
};

const dltAnnotation = () => {
  deleteAnnotation(annotation.value!);
  clear();
};

const annotation = ref<RevisionAnnotation | undefined>();

watch(annotationBeingEdited, (newVal) => {
  if (annotationBeingEdited.value != undefined) {
    annotation.value = { ...annotationBeingEdited.value! };
  }
});

// I see I was pulling from a store and clearing hte comments. I see instead I really jsut need to know the active step criteria
// and then I can pull the comments from the annotation definition
const systemCommentsStore = useSystemCommentsStore();
const { filteredComments } = storeToRefs(systemCommentsStore);

const checklistController = useChecklistControllerStore(documentId);
const { currentCriteria } = storeToRefs(checklistController);

const commentsList = computed(() => {
  let comments = [] as string[];

  if (currentCriteria.value) {
    if (currentCriteria.value.quickCommentTag == undefined) {
      comments = currentCriteria.value.quickComments ?? [];
    }

    if (currentCriteria.value.quickCommentTag) {
      comments = filteredComments
        .value({
          tag: currentCriteria.value.quickCommentTag,
          documentId: documentId,
        })
        .map((c) => c.comment);
    }
  }

  if (annotation.value) {
    const definition = annotationDefinition.value(annotation.value.type);

    if (definition?.quickComments) {
      comments = [...comments, ...definition.quickComments];
    }
  }

  return comments;
});

const commentOnlyAnnotation = createDefaultAnnotation(
  useAnnotationType(AnnotationTypes.commentOnly)!
);

const leftCoordinates = ref();

const calculateLeftCoordinates = () => {
  leftCoordinates.value = useDocumentContainerRightPosition() - 82 + "px";
};

calculateLeftCoordinates();

onMounted(() => {
  window.addEventListener("resize", calculateLeftCoordinates);
  calculateLeftCoordinates();
});

onUnmounted(() => {
  window.addEventListener("resize", calculateLeftCoordinates);
});

const nuxtApp = useNuxtApp();

const changeAnnotationType = (type: AnnotationTypes) => {
  if (annotation.value == undefined) return;

  const oldType = annotation.value?.type;

  if (useAnnotationType(oldType)?.inlineSymbol) {
    // @ts-ignore
    annotation.value.commentObject = nuxtApp.$removePrefixFromQuillDelta(
      annotation.value.commentObject,
      `${useAnnotationType(oldType)!.inlineSymbol} `
    );
  }

  annotation.value!.type = type;

  if (useAnnotationType(type)?.inlineSymbol) {
    if (annotation.value.commentObject == undefined) {
      // @ts-ignore
      annotation.value.commentObject = nuxtApp.$createQuillDelta(
        useAnnotationType(type)!.inlineSymbol
      ).ops;
    } else {
      // @ts-ignore
      annotation.value.commentObject = nuxtApp.$prependToQuillDelta(
        annotation.value.commentObject,
        useAnnotationType(type)!.inlineSymbol
      ).ops;
    }
  }
};

const onCommentClicked = (event: string) => {
  if (!annotation.value) return;

  annotation.value.quickComment = event;
};
</script>

<template>
  <div class="fixed top-0 left-0 w-screen h-screen z-[90] pointer-events-none">
    <Transition>
      <div
        v-if="annotation"
        class="absolute top-[60px] p-4 z-[100] pointer-events-auto"
        data-step-id="annotation-form"
        :style="{
          height: 'calc(100% - 60px)',
          left: leftCoordinates,
        }"
        @click.stop
        @contextmenu.stop
      >
        <div
          class="flex flex-col rounded shadow bg-white p-4 w-[500px] h-full overflow-auto pb-[96px]"
        >
          <h2 class="text-h4">Comment</h2>
          <div class="flex flex-row items-center my-2">
            <BaseCheckbox
              v-model="annotation.forceInline"
              label="Force this comment inline"
            />
          </div>
          <!-- <div v-if="activeAnnotations" class="flex flex-row flex-wrap">
            <AnnotationFormAnnotationButton
              :key="-1"
              @click="changeAnnotationType(AnnotationTypes.commentOnly)"
              :annotation="commentOnlyAnnotation"
              v-tooltip="{
                content: commentOnlyAnnotation.type,
                delay: 400,
              }"
              :is-selected="annotation?.type == AnnotationTypes.commentOnly"
            ></AnnotationFormAnnotationButton>
            <AnnotationFormAnnotationButton
              v-for="(an, index) in activeAnnotations"
              :key="index"
              @click="changeAnnotationType(an.type)"
              :annotation="an"
              v-tooltip="{
                content: useAnnotationType(an.type)?.hint,
                delay: 400,
              }"
              :is-selected="annotation?.type == an.type"
            ></AnnotationFormAnnotationButton>
          </div> -->
          <BaseDocumentTextEditor
            :key="annotation.type"
            v-model="annotation!.commentObject"
            class="mb-2 max-h-[400px]"
            :auto-focus="true"
            :quick-comments-list="commentsList"
            :show-comments-list="true"
            @update:model-value="annotation.commentObject = $event"
            @on-save="save"
            @on-close="clear"
            @on-select-comment-from-list="onCommentClicked"
          />
        </div>
        <div
          class="absolute bottom-0 left-0 w-full mt-0 items-center justify-between px-4"
        >
          <div
            class="flex flex-row items-center border-t bg-surface p-4 justify-between w-full"
          >
            <span class="flex flex-row items-center">
              <BaseButton
                class="w-[140px] mr-2"
                data-step-id="annotation-form-save-button"
                @click="save()"
                >Save</BaseButton
              >
              <BaseTextButton
                color="danger"
                data-step-id="annotation-form-cancel-button"
                @click="clear"
                >Cancel</BaseTextButton
              >
            </span>
            <BaseButton
              v-if="annotation?.id"
              data-step-id="annotation-form-delete-button"
              color="danger"
              class="h-[42px] "
              @click="dltAnnotation"
            >
              <div class="flex flex-row items-center">
                <BaseIconDelete class="mr-1" />
                Delete
              </div>
            </BaseButton>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: all 0.25s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;

  transform: translateX(200%);
}

/* .v-enter-to,
.v-leave-from {
  opacity: 1.0;
  transform: translateX(100%);
} */
</style>
